import { Badge, type BadgeProps } from '@atlas/ui-primitives'
import { ARTICLE_TRANSLATION_STATUS_LABELS, ArticleTranslationStatus } from '@/domain/article.types'

/**
 * The ONLY translation-status-to-badge mapping in the app. Both the locale strip
 * and the articles index render this component rather than each keeping its own
 * lookup, since two surfaces disagreeing about what "translating" looks like is
 * the failure a second copy invites.
 */
const STATUS_VARIANT: Readonly<Record<ArticleTranslationStatus, NonNullable<BadgeProps['variant']>>> = {
	[ArticleTranslationStatus.PENDING]: 'muted',
	[ArticleTranslationStatus.TRANSLATING]: 'primary',
	[ArticleTranslationStatus.READY]: 'success',
	[ArticleTranslationStatus.FAILED]: 'danger',
}

/** Copy for a job past the stuck threshold — deliberately not "Failed". */
export const TAKING_LONGER_LABEL = 'Taking longer than expected'

export interface ArticleTranslationBadgeProps {
	status: ArticleTranslationStatus | null
	/**
	 * Whether the job has sat unchanged past `ARTICLE_TRANSLATION_STUCK_AFTER_MS`.
	 * Resolved by the caller from the shared predicate, never recomputed here, so
	 * one threshold governs both this badge and the polling stop condition.
	 */
	isStuck?: boolean
	size?: BadgeProps['size']
}

/**
 * A translation job's state as a badge.
 *
 * Renders nothing for a null status: null means the variant was authored rather
 * than machine-translated, and a badge standing in for that absence would read
 * as a job state it never had.
 *
 * A stuck job is shown as taking longer, not as failed. The article worker's
 * queue can still redeliver and complete it up to its visibility timeout, so
 * asserting failure would be wrong as often as it was right.
 */
export function ArticleTranslationBadge({ status, isStuck = false, size = 'sm' }: ArticleTranslationBadgeProps) {
	if (!status) return null

	const showsStalled =
		isStuck && status !== ArticleTranslationStatus.READY && status !== ArticleTranslationStatus.FAILED

	return (
		<Badge variant={showsStalled ? 'danger' : STATUS_VARIANT[status]} size={size}>
			{showsStalled ? TAKING_LONGER_LABEL : ARTICLE_TRANSLATION_STATUS_LABELS[status]}
		</Badge>
	)
}
